interface Map<T> {
  [key: string]: T[];
}

function formatTreeData<T extends Map<T>, U>(
  data: T[] | undefined | null,
  formatter: (value: T, index?: number, parent?: U) => U | void | undefined,
  childrenField: string = "children",
  resultChildrenField: string = "children",
  parent?: U
): U[] {
  const result = <Array<U>>[];
  data == null
    ? void 0
    : data.forEach((d, i) => {
        let _a;
        const item = formatter(d, i, parent);
        if (item) {
          if ((_a = d[childrenField]) == null ? void 0 : _a.length) {
            (item as any)[resultChildrenField] = formatTreeData(
              d[childrenField],
              formatter,
              childrenField,
              resultChildrenField,
              item
            );
          }
          result.push(item);
        }
      });
  return result;
}

function isExternalLink(url: string): boolean {
  return !!(
    url &&
    (url.startsWith("http://") ||
      url.startsWith("https://") ||
      url.startsWith("//"))
  );
}
/**
 * parentId 形式数据转 children 形式参数
 */
interface ToTreeDataOption<T> {
  /**
   * 数据
   */
  data?: T[] | null;
  /**
   * id 字段名称
   */
  idField?: string | null;
  /**
   * parentId 字段名称
   */
  parentIdField?: string | null;
  /**
   * 生成的 children 字段名称
   */
  childrenField?: string | null;
  /**
   * 最顶级的 parentId 值
   */
  parentId?: number | string | Array<number | string> | null;
  /**
   * 是否添加包含所有父级 id 的字段
   */
  addParentIds?: boolean | null;
  /**
   * 包含所有父级 id 字段的名称
   */
  parentIdsField?: string | null;
  /**
   * 所有父级的 id
   */
  parentIds?: Array<number | string> | null;
}
function toTreeData<T extends Map<T>>(option: ToTreeDataOption<T>): T[] {
  const data = option.data;
  const idField = option.idField || "id";
  const parentIdField = option.parentIdField || "parentId";
  const childrenField = option.childrenField || "children";
  const parentIdIsNull = option.parentId == null;
  const parentId = parentIdIsNull ? [] : option.parentId;
  const addParentIds = option.addParentIds;
  const parentIdsField = option.parentIdsField || "parentIds";
  const parentIds = option.parentIds;
  if (data == null) {
    return [];
  }
  if (parentIdIsNull) {
    data.forEach((d) => {
      let flag = true;
      for (let i = 0; i < data.length; i++) {
        if (d[parentIdField] == data[i][idField]) {
          flag = false;
          break;
        }
      }
      if (flag) {
        (parentId as any[]).push(d[parentIdField]);
      }
    });
  }
  const result: T[] = [];
  data.forEach((d) => {
    if (d[idField] == d[parentIdField]) {
      throw new Error(
        [
          "data error: {",
          idField + ": ",
          JSON.stringify(d[idField]),
          parentIdField + ": ",
          JSON.stringify(d[parentIdField]),
          "}",
        ].join("")
      );
    }
    if (
      Array.isArray(parentId)
        ? (parentId as any).includes(d[parentIdField])
        : (d[parentIdField] as any) == parentId
    ) {
      const r = { ...d };
      const children = toTreeData({
        data,
        idField,
        parentIdField,
        childrenField,
        parentId: d[idField] as any,
        addParentIds,
        parentIdsField,
        parentIds: (parentIds != null ? parentIds : []).concat([
          (d as any)[idField],
        ]),
      });
      if (children.length > 0) {
        (r as Map<T>)[childrenField] = children;
      }
      if (addParentIds) {
        (r as Map<any>)[parentIdsField] = parentIds != null ? parentIds : [];
      }
      result.push(r);
    }
  });
  return result;
}
function eachTreeData<T extends Map<T>>(
  data?: T[],
  callback?: (value: T, index: number, parent?: T) => void | boolean,
  childrenField: string = "children",
  parent?: T
) {
  if (data) {
    data.forEach((d, i) => {
      let _a;
      if (
        callback &&
        callback(d, i, parent) !== false &&
        ((_a = d[childrenField]) == null ? void 0 : _a.length)
      ) {
        eachTreeData(d[childrenField], callback, childrenField, d);
      }
    });
  }
}

export { formatTreeData, isExternalLink, toTreeData, eachTreeData };
